import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import styles from './TripTools.module.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ============================================================
   TOOL DATA (sidebar items)
   ============================================================ */
const TOOLS = [
    { id: 'packing', icon: '🎒', label: 'Packing List', color: '#8b5cf6' },
    { id: 'currency', icon: '💱', label: 'Currency Converter', color: '#10b981' },
    { id: 'phrases', icon: '🗣️', label: 'Travel Phrases', color: '#3b82f6' },
    { id: 'documents', icon: '📄', label: 'Trip Documents', color: '#ec4899' },
    { id: 'emergency', icon: '🚨', label: 'Emergency Info', color: '#ef4444' },
    { id: 'culture', icon: '🏛️', label: 'Culture Tips', color: '#f97316' },
];

/* ============================================================
   1. PACKING LIST TOOL (per-trip list + AI packing assistant)
   ============================================================ */
const PACKING_CATEGORIES = ['Essentials', 'Documents', 'Ritual', 'Clothes', 'Toiletries', 'Health', 'Electronics', 'Activity Gear', 'Extras'];

const PACKING_QUICK_REPLIES = [
    '🧳 Build my packing list',
    "🏊 I'm going to swim",
    '🥾 Add hiking gear',
    '✈️ Carry-on only',
];

// The assistant writes light markdown, the same way the travel guide chatbot does.
function packingReplyToHtml(text) {
    const html = text
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\n- /g, '<br/>• ')
        .replace(/\n\n/g, '<br/><br/>')
        .replace(/\n/g, '<br/>');

    return DOMPurify.sanitize(html);
}

function PackingListTool() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [newItem, setNewItem] = useState('');
    const [newItemCategory, setNewItemCategory] = useState('Essentials');
    // Item names the assistant just added, so they can flash in the list.
    const [justAdded, setJustAdded] = useState([]);

    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => {
        const loadPackingList = async () => {
            try {
                const response = await fetch(`${API_URL}/packing`, { credentials: 'include' });
                const data = await response.json();

                if (!response.ok) {
                    setLoadError(data.error || 'Could not load your packing list');
                    return;
                }

                setItems(data.items);
                // An empty list means the traveler hasn't started yet, so lead with the assistant.
                setChatOpen(data.items.length === 0);
            } catch (error) {
                console.error('Error loading packing list:', error);
                setLoadError('Could not reach the server');
            } finally {
                setLoading(false);
            }
        };

        loadPackingList();
    }, []);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [chatMessages, chatLoading]);

    useEffect(() => {
        if (justAdded.length === 0) return;

        const timer = setTimeout(() => setJustAdded([]), 3000);
        return () => clearTimeout(timer);
    }, [justAdded]);

    const sendToAssistant = async (text) => {
        const message = text.trim();
        if (!message || chatLoading) return;

        const history = chatMessages.slice(-15);
        setChatMessages(prev => [...prev, { role: 'user', content: message }]);
        setChatInput('');
        setChatOpen(true);
        setChatLoading(true);

        try {
            const response = await fetch(`${API_URL}/packing/chat`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message, chatHistory: history })
            });

            const data = await response.json();

            if (!response.ok) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.error || 'Something went wrong. Please try again.' }]);
                return;
            }

            setChatMessages(prev => [...prev, { role: 'assistant', content: data.reply, changes: data.changes }]);

            // The assistant edits the list on the server, so the reply carries the fresh list.
            setItems(data.items);

            if (data.changes?.added?.length > 0) {
                setJustAdded(data.changes.added.map(name => name.toLowerCase()));
            }
        } catch (error) {
            console.error('Packing assistant error:', error);
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'I could not reach the server. Please try again.' }]);
        } finally {
            setChatLoading(false);
        }
    };

    const toggleCheck = async (target) => {
        const nextChecked = !target.checked;

        setItems(prev => prev.map(item => item.id === target.id ? { ...item, checked: nextChecked } : item));

        try {
            const response = await fetch(`${API_URL}/packing/${target.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ checked: nextChecked })
            });

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}`);
            }
        } catch (error) {
            console.error('Could not save the checkbox:', error);
            setItems(prev => prev.map(item => item.id === target.id ? { ...item, checked: target.checked } : item));
        }
    };

    const removeItem = async (target) => {
        try {
            const response = await fetch(`${API_URL}/packing/${target.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}`);
            }

            setItems(prev => prev.filter(item => item.id !== target.id));
        } catch (error) {
            console.error('Could not remove the item:', error);
        }
    };

    const addItem = async () => {
        const name = newItem.trim();
        if (!name) return;

        setNewItem('');

        try {
            const response = await fetch(`${API_URL}/packing`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ item: name, category: newItemCategory })
            });

            const data = await response.json();

            if (response.ok && data.item) {
                setItems(prev => [...prev, data.item]);
            }
        } catch (error) {
            console.error('Could not add the item:', error);
        }
    };

    const clearList = async () => {
        if (!window.confirm('Delete every item on this packing list?')) return;

        try {
            const response = await fetch(`${API_URL}/packing`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}`);
            }

            setItems([]);
        } catch (error) {
            console.error('Could not clear the list:', error);
        }
    };

    const checkedCount = items.filter(item => item.checked).length;
    const progress = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;

    const itemsByCategory = {};
    for (const item of items) {
        if (!itemsByCategory[item.category]) {
            itemsByCategory[item.category] = [];
        }
        itemsByCategory[item.category].push(item);
    }
    const filledCategories = PACKING_CATEGORIES.filter(category => itemsByCategory[category]);

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <span className={styles.toolHeaderEmoji}>🎒</span>
                <div>
                    <h2>Packing List</h2>
                    <p>{items.length > 0 ? `${checkedCount} of ${items.length} items packed` : 'Built for this trip by your AI assistant'}</p>
                </div>
            </div>

            {/* Progress bar */}
            <div className={styles.progressSection}>
                <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${progress}%`, background: progress === 100 ? '#22c55e' : '#8b5cf6' }}></div>
                </div>
                <span className={styles.progressText}>{progress}%</span>
            </div>

            {/* AI packing assistant */}
            <div className={styles.aiPanel}>
                <button className={styles.aiPanelHeader} onClick={() => setChatOpen(open => !open)}>
                    <img src="/ai_assistant.svg" alt="" className={styles.aiAvatar} />
                    <div className={styles.aiPanelTitle}>
                        <strong>Packing Assistant</strong>
                        <span>Tell it your plans and it fills the list</span>
                    </div>
                    <span className={`material-symbols-outlined ${styles.aiPanelChevron}`}>
                        {chatOpen ? 'expand_less' : 'expand_more'}
                    </span>
                </button>

                {chatOpen && (
                    <div className={styles.aiBody}>
                        <div className={styles.aiMessages}>
                            {chatMessages.length === 0 && (
                                <div className={styles.aiWelcome}>
                                    <p>Hi! I know where and when you&apos;re going — tell me what you&apos;ll be doing and I&apos;ll build the list.</p>
                                    <p className={styles.aiWelcomeHint}>Try &quot;hey, I&apos;m going to swim&quot; and I&apos;ll add everything you need for it.</p>
                                </div>
                            )}

                            {chatMessages.map((message, index) => (
                                <div
                                    key={index}
                                    className={`${styles.aiBubble} ${message.role === 'user' ? styles.aiBubbleUser : styles.aiBubbleBot}`}
                                >
                                    {message.role === 'user' ? (
                                        <p>{message.content}</p>
                                    ) : (
                                        <>
                                            <div dangerouslySetInnerHTML={{ __html: packingReplyToHtml(message.content) }} />
                                            {message.changes?.added?.length > 0 && (
                                                <span className={styles.aiChangeBadge}>
                                                    <span className="material-symbols-outlined">add_circle</span>
                                                    {message.changes.added.length} added to your list
                                                </span>
                                            )}
                                            {message.changes?.removed?.length > 0 && (
                                                <span className={`${styles.aiChangeBadge} ${styles.aiChangeBadgeRemoved}`}>
                                                    <span className="material-symbols-outlined">do_not_disturb_on</span>
                                                    {message.changes.removed.length} removed
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}

                            {chatLoading && (
                                <div className={`${styles.aiBubble} ${styles.aiBubbleBot}`}>
                                    <div className={styles.aiTyping}>
                                        <span></span><span></span><span></span>
                                    </div>
                                </div>
                            )}

                            <div ref={chatEndRef} />
                        </div>

                        <div className={styles.aiChips}>
                            {PACKING_QUICK_REPLIES.map(reply => (
                                <button
                                    key={reply}
                                    className={styles.aiChip}
                                    onClick={() => sendToAssistant(reply.replace(/^\S+\s/, ''))}
                                    disabled={chatLoading}
                                >
                                    {reply}
                                </button>
                            ))}
                        </div>

                        <form
                            className={styles.aiInputRow}
                            onSubmit={(e) => { e.preventDefault(); sendToAssistant(chatInput); }}
                        >
                            <input
                                className={styles.aiInput}
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                placeholder="Tell me what you'll be doing..."
                                disabled={chatLoading}
                            />
                            <button type="submit" className={styles.aiSend} disabled={chatLoading || !chatInput.trim()}>
                                <span className="material-symbols-outlined">send</span>
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* The list itself */}
            <div className={styles.packingCategories}>
                {loading && <p className={styles.packingNotice}>Loading your list...</p>}

                {!loading && loadError && <p className={styles.packingNotice}>{loadError}</p>}

                {!loading && !loadError && items.length === 0 && (
                    <p className={styles.packingNotice}>
                        Nothing packed yet. Ask the assistant above to build your list, or add items yourself.
                    </p>
                )}

                {filledCategories.map(category => {
                    const categoryItems = itemsByCategory[category];
                    const categoryChecked = categoryItems.filter(item => item.checked).length;

                    return (
                        <div key={category} className={styles.packingCategory}>
                            <h3 className={styles.packingCatTitle}>{category}
                                <span className={styles.packingCatCount}>{categoryChecked}/{categoryItems.length}</span>
                            </h3>
                            <div className={styles.packingItems}>
                                {categoryItems.map(item => (
                                    <div
                                        key={item.id}
                                        className={`${styles.packingItem} ${item.checked ? styles.packingItemChecked : ''} ${justAdded.includes(item.item.toLowerCase()) ? styles.packingItemNew : ''}`}
                                    >
                                        <button className={styles.packingCheckbox} onClick={() => toggleCheck(item)}>
                                            <span className="material-symbols-outlined">
                                                {item.checked ? 'check_circle' : 'radio_button_unchecked'}
                                            </span>
                                        </button>
                                        <span className={styles.packingItemText}>
                                            {item.item}
                                            {item.quantity > 1 && <span className={styles.packingQty}>×{item.quantity}</span>}
                                            {item.reason && <span className={styles.packingReason}>{item.reason}</span>}
                                        </span>
                                        <button className={styles.packingRemove} onClick={() => removeItem(item)}>
                                            <span className="material-symbols-outlined">close</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Add your own */}
                <div className={styles.packingAddRow}>
                    <select
                        className={styles.packingCategorySelect}
                        value={newItemCategory}
                        onChange={(e) => setNewItemCategory(e.target.value)}
                    >
                        {PACKING_CATEGORIES.map(category => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>
                    <input
                        className={styles.packingAddInput}
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        placeholder="Add an item yourself..."
                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                    />
                    <button className={styles.packingAddConfirm} onClick={addItem}>
                        <span className="material-symbols-outlined">add</span>
                    </button>
                </div>

                {items.length > 0 && (
                    <button className={styles.packingClearBtn} onClick={clearList}>
                        <span className="material-symbols-outlined">delete_sweep</span> Clear the whole list
                    </button>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   2. CURRENCY CONVERTER TOOL
   ============================================================ */
const DEFAULT_CURRENCIES = [
    { code: 'USD', name: 'US Dollar', flag: '🇺🇸', symbol: '$' },
    { code: 'EUR', name: 'Euro', flag: '🇪🇺', symbol: '€' },
    { code: 'GBP', name: 'British Pound', flag: '🇬🇧', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵', symbol: '¥' },
    { code: 'CAD', name: 'Canadian Dollar', flag: '🇨🇦', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺', symbol: 'A$' },
    { code: 'CHF', name: 'Swiss Franc', flag: '🇨🇭', symbol: 'Fr' },
    { code: 'ILS', name: 'Israeli Shekel', flag: '🇮🇱', symbol: '₪' },
    { code: 'INR', name: 'Indian Rupee', flag: '🇮🇳', symbol: '₹' },
    { code: 'MXN', name: 'Mexican Peso', flag: '🇲🇽', symbol: '$' },
    { code: 'BRL', name: 'Brazilian Real', flag: '🇧🇷', symbol: 'R$' },
    { code: 'THB', name: 'Thai Baht', flag: '🇹🇭', symbol: '฿' },
    { code: 'KRW', name: 'South Korean Won', flag: '🇰🇷', symbol: '₩' },
    { code: 'SGD', name: 'Singapore Dollar', flag: '🇸🇬', symbol: 'S$' },
    { code: 'TRY', name: 'Turkish Lira', flag: '🇹🇷', symbol: '₺' },
];

const ALL_CURRENCIES = [
    { code: 'AED', name: 'UAE Dirham', flag: '🇦🇪', symbol: 'د.إ' },
    { code: 'AFN', name: 'Afghan Afghani', flag: '🇦🇫', symbol: '؋' },
    { code: 'ALL', name: 'Albanian Lek', flag: '🇦🇱', symbol: 'L' },
    { code: 'AMD', name: 'Armenian Dram', flag: '🇦🇲', symbol: '֏' },
    { code: 'ANG', name: 'Netherlands Antillean Guilder', flag: '🇨🇼', symbol: 'ƒ' },
    { code: 'AOA', name: 'Angolan Kwanza', flag: '🇦🇴', symbol: 'Kz' },
    { code: 'ARS', name: 'Argentine Peso', flag: '🇦🇷', symbol: '$' },
    { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺', symbol: 'A$' },
    { code: 'AWG', name: 'Aruban Florin', flag: '🇦🇼', symbol: 'ƒ' },
    { code: 'AZN', name: 'Azerbaijani Manat', flag: '🇦🇿', symbol: '₼' },
    { code: 'BAM', name: 'Bosnia-Herzegovina Mark', flag: '🇧🇦', symbol: 'KM' },
    { code: 'BBD', name: 'Barbadian Dollar', flag: '🇧🇧', symbol: '$' },
    { code: 'BDT', name: 'Bangladeshi Taka', flag: '🇧🇩', symbol: '৳' },
    { code: 'BGN', name: 'Bulgarian Lev', flag: '🇧🇬', symbol: 'лв' },
    { code: 'BHD', name: 'Bahraini Dinar', flag: '🇧🇭', symbol: '.د.ب' },
    { code: 'BIF', name: 'Burundian Franc', flag: '🇧🇮', symbol: 'FBu' },
    { code: 'BMD', name: 'Bermudian Dollar', flag: '🇧🇲', symbol: '$' },
    { code: 'BND', name: 'Brunei Dollar', flag: '🇧🇳', symbol: '$' },
    { code: 'BOB', name: 'Bolivian Boliviano', flag: '🇧🇴', symbol: 'Bs.' },
    { code: 'BRL', name: 'Brazilian Real', flag: '🇧🇷', symbol: 'R$' },
    { code: 'BSD', name: 'Bahamian Dollar', flag: '🇧🇸', symbol: '$' },
    { code: 'BTN', name: 'Bhutanese Ngultrum', flag: '🇧🇹', symbol: 'Nu.' },
    { code: 'BWP', name: 'Botswanan Pula', flag: '🇧🇼', symbol: 'P' },
    { code: 'BYN', name: 'Belarusian Ruble', flag: '🇧🇾', symbol: 'Br' },
    { code: 'BZD', name: 'Belize Dollar', flag: '🇧🇿', symbol: 'BZ$' },
    { code: 'CAD', name: 'Canadian Dollar', flag: '🇨🇦', symbol: 'C$' },
    { code: 'CDF', name: 'Congolese Franc', flag: '🇨🇩', symbol: 'FC' },
    { code: 'CHF', name: 'Swiss Franc', flag: '🇨🇭', symbol: 'Fr' },
    { code: 'CLP', name: 'Chilean Peso', flag: '🇨🇱', symbol: '$' },
    { code: 'CNY', name: 'Chinese Yuan', flag: '🇨🇳', symbol: '¥' },
    { code: 'COP', name: 'Colombian Peso', flag: '🇨🇴', symbol: '$' },
    { code: 'CRC', name: 'Costa Rican Colón', flag: '🇨🇷', symbol: '₡' },
    { code: 'CUP', name: 'Cuban Peso', flag: '🇨🇺', symbol: '₱' },
    { code: 'CVE', name: 'Cape Verdean Escudo', flag: '🇨🇻', symbol: '$' },
    { code: 'CZK', name: 'Czech Koruna', flag: '🇨🇿', symbol: 'Kč' },
    { code: 'DJF', name: 'Djiboutian Franc', flag: '🇩🇯', symbol: 'Fdj' },
    { code: 'DKK', name: 'Danish Krone', flag: '🇩🇰', symbol: 'kr' },
    { code: 'DOP', name: 'Dominican Peso', flag: '🇩🇴', symbol: 'RD$' },
    { code: 'DZD', name: 'Algerian Dinar', flag: '🇩🇿', symbol: 'د.ج' },
    { code: 'EGP', name: 'Egyptian Pound', flag: '🇪🇬', symbol: '£' },
    { code: 'ERN', name: 'Eritrean Nakfa', flag: '🇪🇷', symbol: 'Nfk' },
    { code: 'ETB', name: 'Ethiopian Birr', flag: '🇪🇹', symbol: 'Br' },
    { code: 'EUR', name: 'Euro', flag: '🇪🇺', symbol: '€' },
    { code: 'FJD', name: 'Fijian Dollar', flag: '🇫🇯', symbol: 'FJ$' },
    { code: 'GBP', name: 'British Pound', flag: '🇬🇧', symbol: '£' },
    { code: 'GEL', name: 'Georgian Lari', flag: '🇬🇪', symbol: '₾' },
    { code: 'GHS', name: 'Ghanaian Cedi', flag: '🇬🇭', symbol: '₵' },
    { code: 'GMD', name: 'Gambian Dalasi', flag: '🇬🇲', symbol: 'D' },
    { code: 'GNF', name: 'Guinean Franc', flag: '🇬🇳', symbol: 'FG' },
    { code: 'GTQ', name: 'Guatemalan Quetzal', flag: '🇬🇹', symbol: 'Q' },
    { code: 'GYD', name: 'Guyanese Dollar', flag: '🇬🇾', symbol: 'GY$' },
    { code: 'HKD', name: 'Hong Kong Dollar', flag: '🇭🇰', symbol: 'HK$' },
    { code: 'HNL', name: 'Honduran Lempira', flag: '🇭🇳', symbol: 'L' },
    { code: 'HRK', name: 'Croatian Kuna', flag: '🇭🇷', symbol: 'kn' },
    { code: 'HTG', name: 'Haitian Gourde', flag: '🇭🇹', symbol: 'G' },
    { code: 'HUF', name: 'Hungarian Forint', flag: '🇭🇺', symbol: 'Ft' },
    { code: 'IDR', name: 'Indonesian Rupiah', flag: '🇮🇩', symbol: 'Rp' },
    { code: 'ILS', name: 'Israeli Shekel', flag: '🇮🇱', symbol: '₪' },
    { code: 'INR', name: 'Indian Rupee', flag: '🇮🇳', symbol: '₹' },
    { code: 'IQD', name: 'Iraqi Dinar', flag: '🇮🇶', symbol: 'ع.د' },
    { code: 'IRR', name: 'Iranian Rial', flag: '🇮🇷', symbol: '﷼' },
    { code: 'ISK', name: 'Icelandic Króna', flag: '🇮🇸', symbol: 'kr' },
    { code: 'JMD', name: 'Jamaican Dollar', flag: '🇯🇲', symbol: 'J$' },
    { code: 'JOD', name: 'Jordanian Dinar', flag: '🇯🇴', symbol: 'د.ا' },
    { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵', symbol: '¥' },
    { code: 'KES', name: 'Kenyan Shilling', flag: '🇰🇪', symbol: 'KSh' },
    { code: 'KGS', name: 'Kyrgystani Som', flag: '🇰🇬', symbol: 'сом' },
    { code: 'KHR', name: 'Cambodian Riel', flag: '🇰🇭', symbol: '៛' },
    { code: 'KRW', name: 'South Korean Won', flag: '🇰🇷', symbol: '₩' },
    { code: 'KWD', name: 'Kuwaiti Dinar', flag: '🇰🇼', symbol: 'د.ك' },
    { code: 'KZT', name: 'Kazakhstani Tenge', flag: '🇰🇿', symbol: '₸' },
    { code: 'LAK', name: 'Laotian Kip', flag: '🇱🇦', symbol: '₭' },
    { code: 'LBP', name: 'Lebanese Pound', flag: '🇱🇧', symbol: 'ل.ل' },
    { code: 'LKR', name: 'Sri Lankan Rupee', flag: '🇱🇰', symbol: 'Rs' },
    { code: 'LRD', name: 'Liberian Dollar', flag: '🇱🇷', symbol: 'L$' },
    { code: 'MAD', name: 'Moroccan Dirham', flag: '🇲🇦', symbol: 'د.م.' },
    { code: 'MDL', name: 'Moldovan Leu', flag: '🇲🇩', symbol: 'L' },
    { code: 'MGA', name: 'Malagasy Ariary', flag: '🇲🇬', symbol: 'Ar' },
    { code: 'MKD', name: 'Macedonian Denar', flag: '🇲🇰', symbol: 'ден' },
    { code: 'MMK', name: 'Myanmar Kyat', flag: '🇲🇲', symbol: 'K' },
    { code: 'MNT', name: 'Mongolian Tugrik', flag: '🇲🇳', symbol: '₮' },
    { code: 'MOP', name: 'Macanese Pataca', flag: '🇲🇴', symbol: 'MOP$' },
    { code: 'MRU', name: 'Mauritanian Ouguiya', flag: '🇲🇷', symbol: 'UM' },
    { code: 'MUR', name: 'Mauritian Rupee', flag: '🇲🇺', symbol: '₨' },
    { code: 'MVR', name: 'Maldivian Rufiyaa', flag: '🇲🇻', symbol: 'Rf' },
    { code: 'MWK', name: 'Malawian Kwacha', flag: '🇲🇼', symbol: 'MK' },
    { code: 'MXN', name: 'Mexican Peso', flag: '🇲🇽', symbol: '$' },
    { code: 'MYR', name: 'Malaysian Ringgit', flag: '🇲🇾', symbol: 'RM' },
    { code: 'MZN', name: 'Mozambican Metical', flag: '🇲🇿', symbol: 'MT' },
    { code: 'NAD', name: 'Namibian Dollar', flag: '🇳🇦', symbol: 'N$' },
    { code: 'NGN', name: 'Nigerian Naira', flag: '🇳🇬', symbol: '₦' },
    { code: 'NIO', name: 'Nicaraguan Córdoba', flag: '🇳🇮', symbol: 'C$' },
    { code: 'NOK', name: 'Norwegian Krone', flag: '🇳🇴', symbol: 'kr' },
    { code: 'NPR', name: 'Nepalese Rupee', flag: '🇳🇵', symbol: '₨' },
    { code: 'NZD', name: 'New Zealand Dollar', flag: '🇳🇿', symbol: 'NZ$' },
    { code: 'OMR', name: 'Omani Rial', flag: '🇴🇲', symbol: 'ر.ع.' },
    { code: 'PAB', name: 'Panamanian Balboa', flag: '🇵🇦', symbol: 'B/.' },
    { code: 'PEN', name: 'Peruvian Sol', flag: '🇵🇪', symbol: 'S/.' },
    { code: 'PGK', name: 'Papua New Guinean Kina', flag: '🇵🇬', symbol: 'K' },
    { code: 'PHP', name: 'Philippine Peso', flag: '🇵🇭', symbol: '₱' },
    { code: 'PKR', name: 'Pakistani Rupee', flag: '🇵🇰', symbol: '₨' },
    { code: 'PLN', name: 'Polish Zloty', flag: '🇵🇱', symbol: 'zł' },
    { code: 'PYG', name: 'Paraguayan Guarani', flag: '🇵🇾', symbol: '₲' },
    { code: 'QAR', name: 'Qatari Riyal', flag: '🇶🇦', symbol: 'ر.ق' },
    { code: 'RON', name: 'Romanian Leu', flag: '🇷🇴', symbol: 'lei' },
    { code: 'RSD', name: 'Serbian Dinar', flag: '🇷🇸', symbol: 'din.' },
    { code: 'RUB', name: 'Russian Ruble', flag: '🇷🇺', symbol: '₽' },
    { code: 'RWF', name: 'Rwandan Franc', flag: '🇷🇼', symbol: 'RF' },
    { code: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', symbol: 'ر.س' },
    { code: 'SCR', name: 'Seychellois Rupee', flag: '🇸🇨', symbol: '₨' },
    { code: 'SDG', name: 'Sudanese Pound', flag: '🇸🇩', symbol: 'ج.س.' },
    { code: 'SEK', name: 'Swedish Krona', flag: '🇸🇪', symbol: 'kr' },
    { code: 'SGD', name: 'Singapore Dollar', flag: '🇸🇬', symbol: 'S$' },
    { code: 'SLL', name: 'Sierra Leonean Leone', flag: '🇸🇱', symbol: 'Le' },
    { code: 'SOS', name: 'Somali Shilling', flag: '🇸🇴', symbol: 'Sh' },
    { code: 'SRD', name: 'Surinamese Dollar', flag: '🇸🇷', symbol: '$' },
    { code: 'STN', name: 'São Tomé Dobra', flag: '🇸🇹', symbol: 'Db' },
    { code: 'SYP', name: 'Syrian Pound', flag: '🇸🇾', symbol: '£' },
    { code: 'SZL', name: 'Swazi Lilangeni', flag: '🇸🇿', symbol: 'E' },
    { code: 'THB', name: 'Thai Baht', flag: '🇹🇭', symbol: '฿' },
    { code: 'TJS', name: 'Tajikistani Somoni', flag: '🇹🇯', symbol: 'SM' },
    { code: 'TMT', name: 'Turkmenistani Manat', flag: '🇹🇲', symbol: 'T' },
    { code: 'TND', name: 'Tunisian Dinar', flag: '🇹🇳', symbol: 'د.ت' },
    { code: 'TOP', name: 'Tongan Paʻanga', flag: '🇹🇴', symbol: 'T$' },
    { code: 'TRY', name: 'Turkish Lira', flag: '🇹🇷', symbol: '₺' },
    { code: 'TTD', name: 'Trinidad & Tobago Dollar', flag: '🇹🇹', symbol: 'TT$' },
    { code: 'TWD', name: 'New Taiwan Dollar', flag: '🇹🇼', symbol: 'NT$' },
    { code: 'TZS', name: 'Tanzanian Shilling', flag: '🇹🇿', symbol: 'TSh' },
    { code: 'UAH', name: 'Ukrainian Hryvnia', flag: '🇺🇦', symbol: '₴' },
    { code: 'UGX', name: 'Ugandan Shilling', flag: '🇺🇬', symbol: 'USh' },
    { code: 'USD', name: 'US Dollar', flag: '🇺🇸', symbol: '$' },
    { code: 'UYU', name: 'Uruguayan Peso', flag: '🇺🇾', symbol: '$U' },
    { code: 'UZS', name: 'Uzbekistani Som', flag: '🇺🇿', symbol: 'сўм' },
    { code: 'VES', name: 'Venezuelan Bolívar', flag: '🇻🇪', symbol: 'Bs.' },
    { code: 'VND', name: 'Vietnamese Dong', flag: '🇻🇳', symbol: '₫' },
    { code: 'VUV', name: 'Vanuatu Vatu', flag: '🇻🇺', symbol: 'VT' },
    { code: 'WST', name: 'Samoan Tala', flag: '🇼🇸', symbol: 'WS$' },
    { code: 'XAF', name: 'Central African CFA', flag: '🌍', symbol: 'FCFA' },
    { code: 'XOF', name: 'West African CFA', flag: '🌍', symbol: 'CFA' },
    { code: 'YER', name: 'Yemeni Rial', flag: '🇾🇪', symbol: '﷼' },
    { code: 'ZAR', name: 'South African Rand', flag: '🇿🇦', symbol: 'R' },
    { code: 'ZMW', name: 'Zambian Kwacha', flag: '🇿🇲', symbol: 'ZK' },
];

function CurrencyTool() {
    const [amount, setAmount] = useState(1);
    const [fromCurrency, setFromCurrency] = useState('USD');
    const [toCurrency, setToCurrency] = useState('EUR');
    const [rates, setRates] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddCurrency, setShowAddCurrency] = useState(false);
    const [currencySearch, setCurrencySearch] = useState('');
    const [activeCurrencies, setActiveCurrencies] = useState(() => {
        const saved = localStorage.getItem('triptools_currencies');
        return saved ? JSON.parse(saved) : DEFAULT_CURRENCIES;
    });
    const searchRef = useRef(null);

    useEffect(() => {
        localStorage.setItem('triptools_currencies', JSON.stringify(activeCurrencies));
    }, [activeCurrencies]);

    useEffect(() => {
        const fetchRates = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCurrency}`);
                if (!res.ok) throw new Error('Failed to fetch');
                const data = await res.json();
                setRates(data.rates);
            } catch {
                setError('Could not load rates. Check your connection.');
            }
            setLoading(false);
        };
        fetchRates();
    }, [fromCurrency]);

    useEffect(() => {
        if (showAddCurrency && searchRef.current) {
            searchRef.current.focus();
        }
    }, [showAddCurrency]);

    const swap = () => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
    };

    const addCurrency = (currency) => {
        if (activeCurrencies.find(c => c.code === currency.code)) return;
        setActiveCurrencies(prev => [...prev, currency]);
    };

    const removeCurrency = (code) => {
        if (activeCurrencies.length <= 2) return; // keep at least 2
        if (code === fromCurrency || code === toCurrency) return;
        setActiveCurrencies(prev => prev.filter(c => c.code !== code));
    };

    const filteredAllCurrencies = ALL_CURRENCIES.filter(c => {
        const search = currencySearch.toLowerCase();
        const alreadyAdded = activeCurrencies.find(ac => ac.code === c.code);
        if (alreadyAdded) return false;
        return c.code.toLowerCase().includes(search) || c.name.toLowerCase().includes(search);
    });

    const converted = rates && rates[toCurrency] ? (amount * rates[toCurrency]).toFixed(2) : '—';
    const rate = rates && rates[toCurrency] ? rates[toCurrency].toFixed(4) : '—';
    const fromC = activeCurrencies.find(c => c.code === fromCurrency) || ALL_CURRENCIES.find(c => c.code === fromCurrency);
    const toC = activeCurrencies.find(c => c.code === toCurrency) || ALL_CURRENCIES.find(c => c.code === toCurrency);

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <span className={styles.toolHeaderEmoji}>💱</span>
                <div>
                    <h2>Currency Converter</h2>
                    <p>Real-time exchange rates</p>
                </div>
            </div>
            <div className={styles.currencyBody}>
                {/* From */}
                <div className={styles.currencyInputGroup}>
                    <label>Amount</label>
                    <div className={styles.currencyRow}>
                        <select value={fromCurrency} onChange={e => setFromCurrency(e.target.value)} className={styles.currencySelect}>
                            {activeCurrencies.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                        </select>
                        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className={styles.currencyInput} min="0" step="0.01" />
                    </div>
                </div>

                {/* Swap */}
                <button className={styles.currencySwap} onClick={swap}>
                    <span className="material-symbols-outlined">swap_vert</span>
                </button>

                {/* To */}
                <div className={styles.currencyInputGroup}>
                    <label>Converted to</label>
                    <div className={styles.currencyRow}>
                        <select value={toCurrency} onChange={e => setToCurrency(e.target.value)} className={styles.currencySelect}>
                            {activeCurrencies.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                        </select>
                        <div className={styles.currencyResult}>
                            {loading ? '...' : error ? '—' : converted}
                        </div>
                    </div>
                </div>

                {/* Rate info */}
                {!loading && !error && (
                    <div className={styles.currencyRateInfo}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: '#10b981' }}>trending_up</span>
                        <span>1 {fromC?.flag} {fromCurrency} = {rate} {toC?.flag} {toCurrency}</span>
                    </div>
                )}
                {error && <p className={styles.currencyError}>{error}</p>}

                {/* Quick amounts */}
                <div className={styles.quickAmounts}>
                    {[10, 50, 100, 500, 1000].map(a => (
                        <button key={a} onClick={() => setAmount(a)} className={`${styles.quickAmountBtn} ${amount == a ? styles.quickAmountActive : ''}`}>
                            {fromC?.symbol}{a}
                        </button>
                    ))}
                </div>

                {/* Active currencies chips + Add button */}
                <div className={styles.currencyChipsSection}>
                    <div className={styles.currencyChipsSectionHeader}>
                        <span className={styles.currencyChipsLabel}>Your currencies</span>
                        <button className={styles.currencyAddToggle} onClick={() => setShowAddCurrency(!showAddCurrency)}>
                            <span className="material-symbols-outlined">{showAddCurrency ? 'close' : 'add'}</span>
                            {showAddCurrency ? 'Close' : 'Add currency'}
                        </button>
                    </div>
                    <div className={styles.currencyChips}>
                        {activeCurrencies.map(c => (
                            <div key={c.code} className={`${styles.currencyChip} ${(c.code === fromCurrency || c.code === toCurrency) ? styles.currencyChipActive : ''}`}>
                                <span>{c.flag} {c.code}</span>
                                {c.code !== fromCurrency && c.code !== toCurrency && activeCurrencies.length > 2 && (
                                    <button className={styles.currencyChipRemove} onClick={() => removeCurrency(c.code)} title="Remove">
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Add Currency Panel */}
                {showAddCurrency && (
                    <div className={styles.currencyAddPanel}>
                        <div className={styles.currencySearchWrap}>
                            <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', color: '#9ca3af' }}>search</span>
                            <input
                                ref={searchRef}
                                className={styles.currencySearchInput}
                                value={currencySearch}
                                onChange={e => setCurrencySearch(e.target.value)}
                                placeholder="Search currencies..."
                            />
                        </div>
                        <div className={styles.currencyAddList}>
                            {filteredAllCurrencies.length === 0 && (
                                <p className={styles.currencyAddEmpty}>No more currencies to add</p>
                            )}
                            {filteredAllCurrencies.slice(0, 30).map(c => (
                                <button key={c.code} className={styles.currencyAddItem} onClick={() => addCurrency(c)}>
                                    <span className={styles.currencyAddFlag}>{c.flag}</span>
                                    <div className={styles.currencyAddInfo}>
                                        <span className={styles.currencyAddCode}>{c.code}</span>
                                        <span className={styles.currencyAddName}>{c.name}</span>
                                    </div>
                                    <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', color: '#10b981' }}>add_circle</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   3. TRAVEL PHRASES TOOL
   ============================================================ */
const PHRASE_CATEGORIES = {
    'Greetings': [
        { en: 'Hello', phonetic: 'Learn the local greeting!' },
        { en: 'Thank you', phonetic: 'Always appreciated' },
        { en: 'Please', phonetic: 'Politeness goes far' },
        { en: 'Goodbye', phonetic: 'Part on a good note' },
        { en: 'Excuse me', phonetic: 'Get attention politely' },
        { en: 'Sorry', phonetic: 'Apologize sincerely' },
    ],
    'Getting Around': [
        { en: 'Where is...?', phonetic: 'Ask for directions' },
        { en: 'How much does this cost?', phonetic: 'Essential for shopping' },
        { en: 'I need a taxi', phonetic: 'Get a ride' },
        { en: 'Where is the bathroom?', phonetic: 'Urgent phrase!' },
        { en: 'Train station', phonetic: 'Find transportation' },
        { en: 'Airport', phonetic: 'Get to your flight' },
    ],
    'Dining': [
        { en: 'I would like...', phonetic: 'Order food' },
        { en: 'The bill, please', phonetic: 'Ask to pay' },
        { en: 'Water', phonetic: 'Stay hydrated' },
        { en: 'Delicious!', phonetic: 'Compliment the chef' },
        { en: 'I am vegetarian', phonetic: 'Dietary needs' },
        { en: 'No spicy, please', phonetic: 'Food preferences' },
    ],
    'Emergency': [
        { en: 'Help!', phonetic: 'Call for assistance' },
        { en: 'I need a doctor', phonetic: 'Medical help' },
        { en: 'Call the police', phonetic: 'Safety first' },
        { en: 'I am lost', phonetic: 'Ask for help' },
    ],
    'My Phrases': [],
};

const LANGUAGES = [
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
    { code: 'th', name: 'Thai', flag: '🇹🇭' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'el', name: 'Greek', flag: '🇬🇷' },
    { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
    { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
    { code: 'cs', name: 'Czech', flag: '🇨🇿' },
    { code: 'da', name: 'Danish', flag: '🇩🇰' },
    { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
    { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
    { code: 'ms', name: 'Malay', flag: '🇲🇾' },
    { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
    { code: 'tl', name: 'Filipino', flag: '🇵🇭' },
];

// TTS language code mapping
const TTS_LANG_MAP = {
    es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', pt: 'pt-PT',
    ja: 'ja-JP', ko: 'ko-KR', zh: 'zh-CN', ar: 'ar-SA', hi: 'hi-IN',
    he: 'he-IL', tr: 'tr-TR', th: 'th-TH', ru: 'ru-RU', nl: 'nl-NL',
    pl: 'pl-PL', sv: 'sv-SE', el: 'el-GR', vi: 'vi-VN', id: 'id-ID',
    uk: 'uk-UA', ro: 'ro-RO', cs: 'cs-CZ', da: 'da-DK', fi: 'fi-FI',
    no: 'no-NO', hu: 'hu-HU', ms: 'ms-MY', sw: 'sw-KE', tl: 'fil-PH',
    en: 'en-US',
};

function PhrasesTool() {
    const [activeCat, setActiveCat] = useState('Greetings');
    const [copied, setCopied] = useState(null);
    const [targetLang, setTargetLang] = useState(() => {
        return localStorage.getItem('triptools_phrase_lang') || 'es';
    });
    const [translations, setTranslations] = useState({});
    const [translating, setTranslating] = useState({});
    const [speaking, setSpeaking] = useState(null);
    const [customPhrases, setCustomPhrases] = useState(() => {
        const saved = localStorage.getItem('triptools_custom_phrases');
        return saved ? JSON.parse(saved) : [];
    });
    const [newPhrase, setNewPhrase] = useState('');
    const [showAddPhrase, setShowAddPhrase] = useState(false);
    const [showLangPicker, setShowLangPicker] = useState(false);
    const [langSearch, setLangSearch] = useState('');

    useEffect(() => {
        localStorage.setItem('triptools_phrase_lang', targetLang);
    }, [targetLang]);

    useEffect(() => {
        localStorage.setItem('triptools_custom_phrases', JSON.stringify(customPhrases));
    }, [customPhrases]);

    // When language changes, clear old translations
    useEffect(() => {
        setTranslations({});
    }, [targetLang]);

    const currentLang = LANGUAGES.find(l => l.code === targetLang);

    const getCurrentPhrases = () => {
        if (activeCat === 'My Phrases') {
            return customPhrases.map(p => ({ en: p, phonetic: 'Your custom phrase' }));
        }
        return PHRASE_CATEGORIES[activeCat] || [];
    };

    const translatePhrase = useCallback(async (phrase) => {
        const cacheKey = `${phrase}::${targetLang}`;
        if (translations[cacheKey]) return; // already translated
        setTranslating(prev => ({ ...prev, [cacheKey]: true }));
        try {
            const res = await fetch(`${API_URL}/translate`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: phrase, from: 'en', to: targetLang }),
            });
            const data = await res.json();
            if (data.translatedText) {
                setTranslations(prev => ({ ...prev, [cacheKey]: data.translatedText }));
            }
        } catch (err) {
            console.error('Translation error:', err);
        }
        setTranslating(prev => ({ ...prev, [cacheKey]: false }));
    }, [targetLang, translations]);

    // Translate all phrases in current category when it changes or lang changes
    useEffect(() => {
        const phrases = getCurrentPhrases();
        phrases.forEach(p => {
            const cacheKey = `${p.en}::${targetLang}`;
            if (!translations[cacheKey] && !translating[cacheKey]) {
                translatePhrase(p.en);
            }
        });
    }, [activeCat, targetLang, customPhrases]);

    const audioRef = useRef(null);

    const speakText = (text, langCode) => {
        // Stop any current playback
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }

        const ttsLang = TTS_LANG_MAP[langCode] || langCode;

        // Check if browser has a voice for this language
        const voices = ('speechSynthesis' in window) ? window.speechSynthesis.getVoices() : [];
        const hasVoice = voices.some(v => v.lang.startsWith(langCode) || v.lang.startsWith(ttsLang));

        if (hasVoice) {
            // Use native browser TTS
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = ttsLang;
            utterance.rate = 0.85;
            utterance.pitch = 1;
            setSpeaking(text);
            utterance.onend = () => setSpeaking(null);
            utterance.onerror = () => {
                // If native TTS fails, fall back to backend proxy
                speakViaBackend(text, langCode);
            };
            window.speechSynthesis.speak(utterance);
        } else {
            // Fallback: backend-proxied Google Translate TTS
            speakViaBackend(text, langCode);
        }
    };

    const speakViaBackend = (text, langCode) => {
        setSpeaking(text);
        const url = `${API_URL}/translate/tts?text=${encodeURIComponent(text)}&lang=${langCode}`;
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setSpeaking(null); audioRef.current = null; };
        audio.onerror = () => { setSpeaking(null); audioRef.current = null; };
        audio.play().catch(() => setSpeaking(null));
    };

    const copyPhrase = (phrase) => {
        navigator.clipboard.writeText(phrase);
        setCopied(phrase);
        setTimeout(() => setCopied(null), 1500);
    };

    const addCustomPhrase = () => {
        if (!newPhrase.trim()) return;
        setCustomPhrases(prev => [...prev, newPhrase.trim()]);
        setNewPhrase('');
        setShowAddPhrase(false);
        // Auto switch to My Phrases
        setActiveCat('My Phrases');
    };

    const removeCustomPhrase = (idx) => {
        setCustomPhrases(prev => prev.filter((_, i) => i !== idx));
    };

    const changeLang = (code) => {
        setTargetLang(code);
        setShowLangPicker(false);
        setLangSearch('');
    };

    const filteredLanguages = LANGUAGES.filter(l => {
        const s = langSearch.toLowerCase();
        return l.name.toLowerCase().includes(s) || l.code.toLowerCase().includes(s);
    });

    const allCategories = Object.keys(PHRASE_CATEGORIES);

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <span className={styles.toolHeaderEmoji}>🗣️</span>
                <div>
                    <h2>Travel Phrases</h2>
                    <p>Translate & listen in any language</p>
                </div>
            </div>
            <div className={styles.phrasesBody}>
                {/* Language Selector */}
                <div className={styles.langSelector}>
                    <span className={styles.langSelectorLabel}>Translate to:</span>
                    <button className={styles.langSelectorBtn} onClick={() => setShowLangPicker(!showLangPicker)}>
                        <span>{currentLang?.flag}</span>
                        <span>{currentLang?.name}</span>
                        <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>
                            {showLangPicker ? 'expand_less' : 'expand_more'}
                        </span>
                    </button>
                </div>

                {/* Language Picker Dropdown */}
                {showLangPicker && (
                    <div className={styles.langPickerPanel}>
                        <div className={styles.langPickerSearch}>
                            <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: '#9ca3af' }}>search</span>
                            <input
                                value={langSearch}
                                onChange={e => setLangSearch(e.target.value)}
                                placeholder="Search language..."
                                className={styles.langPickerInput}
                                autoFocus
                            />
                        </div>
                        <div className={styles.langPickerList}>
                            {filteredLanguages.map(l => (
                                <button
                                    key={l.code}
                                    className={`${styles.langPickerItem} ${l.code === targetLang ? styles.langPickerItemActive : ''}`}
                                    onClick={() => changeLang(l.code)}
                                >
                                    <span>{l.flag}</span>
                                    <span>{l.name}</span>
                                    {l.code === targetLang && <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: '#06a8f9', marginLeft: 'auto' }}>check</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Category Tabs */}
                <div className={styles.phraseTabs}>
                    {allCategories.map(cat => (
                        <button
                            key={cat}
                            className={`${styles.phraseTab} ${activeCat === cat ? styles.phraseTabActive : ''}`}
                            onClick={() => setActiveCat(cat)}
                        >
                            {cat}
                            {cat === 'My Phrases' && customPhrases.length > 0 && (
                                <span className={styles.phraseTabBadge}>{customPhrases.length}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Phrase List */}
                <div className={styles.phraseList}>
                    {getCurrentPhrases().length === 0 && activeCat === 'My Phrases' && !showAddPhrase && (
                        <div className={styles.phraseEmpty}>
                            <span>✏️</span>
                            <p>No custom phrases yet</p>
                            <span className={styles.phraseEmptySub}>Add your own phrases and they'll be translated automatically</span>
                        </div>
                    )}
                    {getCurrentPhrases().map((p, i) => {
                        const cacheKey = `${p.en}::${targetLang}`;
                        const translated = translations[cacheKey];
                        const isTranslating = translating[cacheKey];
                        return (
                            <div key={`${activeCat}-${i}`} className={styles.phraseCard}>
                                <div className={styles.phraseMain}>
                                    <span className={styles.phraseEn}>{p.en}</span>
                                    <div className={styles.phraseTranslated}>
                                        {isTranslating ? (
                                            <span className={styles.phraseTranslating}>
                                                <span className={styles.translatingDot}></span>
                                                <span className={styles.translatingDot}></span>
                                                <span className={styles.translatingDot}></span>
                                            </span>
                                        ) : translated ? (
                                            <span className={styles.phraseTranslatedText}>
                                                {currentLang?.flag} {translated}
                                            </span>
                                        ) : (
                                            <span className={styles.phraseHint}>{p.phonetic}</span>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.phraseActions}>
                                    {/* Listen to English */}
                                    <button
                                        className={`${styles.phraseActionBtn} ${speaking === `en:${p.en}` ? styles.phraseActionBtnActive : ''}`}
                                        onClick={(e) => { e.stopPropagation(); speakText(p.en, 'en'); setSpeaking(`en:${p.en}`); }}
                                        title="Listen in English"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>volume_up</span>
                                        <span className={styles.phraseActionLabel}>EN</span>
                                    </button>
                                    {/* Listen to Translation */}
                                    {translated && (
                                        <button
                                            className={`${styles.phraseActionBtn} ${styles.phraseActionBtnTranslated} ${speaking === `${targetLang}:${p.en}` ? styles.phraseActionBtnActive : ''}`}
                                            onClick={(e) => { e.stopPropagation(); speakText(translated, targetLang); setSpeaking(`${targetLang}:${p.en}`); }}
                                            title={`Listen in ${currentLang?.name}`}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>volume_up</span>
                                            <span className={styles.phraseActionLabel}>{currentLang?.flag}</span>
                                        </button>
                                    )}
                                    {/* Copy translation */}
                                    <button
                                        className={styles.phraseActionBtn}
                                        onClick={(e) => { e.stopPropagation(); copyPhrase(translated || p.en); }}
                                        title="Copy"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                                            {copied === (translated || p.en) ? 'check' : 'content_copy'}
                                        </span>
                                    </button>
                                    {/* Remove (custom only) */}
                                    {activeCat === 'My Phrases' && (
                                        <button
                                            className={`${styles.phraseActionBtn} ${styles.phraseActionBtnDanger}`}
                                            onClick={(e) => { e.stopPropagation(); removeCustomPhrase(i); }}
                                            title="Remove"
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>delete</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Add custom phrase */}
                <div className={styles.phraseAddSection}>
                    {showAddPhrase ? (
                        <div className={styles.phraseAddForm}>
                            <input
                                className={styles.phraseAddInput}
                                value={newPhrase}
                                onChange={e => setNewPhrase(e.target.value)}
                                placeholder="Type your phrase in English..."
                                onKeyDown={e => e.key === 'Enter' && addCustomPhrase()}
                                autoFocus
                            />
                            <div className={styles.phraseAddActions}>
                                <button className={styles.phraseAddCancel} onClick={() => { setShowAddPhrase(false); setNewPhrase(''); }}>Cancel</button>
                                <button className={styles.phraseAddSave} onClick={addCustomPhrase}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>translate</span>
                                    Add & Translate
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button className={styles.phraseAddBtn} onClick={() => setShowAddPhrase(true)}>
                            <span className="material-symbols-outlined">add</span> Add your own phrase
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ============================================================
   4. TRIP DOCUMENTS TOOL
   ============================================================ */
function DocumentsTool() {
    const [docs, setDocs] = useState(() => {
        const saved = localStorage.getItem('triptools_docs');
        return saved ? JSON.parse(saved) : [];
    });
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', type: 'passport', note: '' });

    useEffect(() => {
        localStorage.setItem('triptools_docs', JSON.stringify(docs));
    }, [docs]);

    const addDoc = () => {
        if (!form.name.trim()) return;
        setDocs(prev => [...prev, { ...form, id: Date.now(), date: new Date().toLocaleDateString() }]);
        setForm({ name: '', type: 'passport', note: '' });
        setShowForm(false);
    };

    const removeDoc = (id) => setDocs(prev => prev.filter(d => d.id !== id));

    const docIcons = { passport: '🛂', flight: '✈️', hotel: '🏨', insurance: '🛡️', ticket: '🎫', other: '📎' };

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <span className={styles.toolHeaderEmoji}>📄</span>
                <div>
                    <h2>Trip Documents</h2>
                    <p>{docs.length} document{docs.length !== 1 ? 's' : ''} saved</p>
                </div>
            </div>
            <div className={styles.docsBody}>
                {docs.length === 0 && !showForm && (
                    <div className={styles.docsEmpty}>
                        <span>📁</span>
                        <p>No documents yet</p>
                        <span className={styles.docsEmptySub}>Add your travel documents for quick access</span>
                    </div>
                )}
                {docs.map(doc => (
                    <div key={doc.id} className={styles.docCard}>
                        <span className={styles.docIcon}>{docIcons[doc.type] || '📎'}</span>
                        <div className={styles.docInfo}>
                            <span className={styles.docName}>{doc.name}</span>
                            {doc.note && <span className={styles.docNote}>{doc.note}</span>}
                            <span className={styles.docDate}>Added {doc.date}</span>
                        </div>
                        <button className={styles.docRemove} onClick={() => removeDoc(doc.id)}>
                            <span className="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                ))}
                {showForm ? (
                    <div className={styles.docForm}>
                        <input placeholder="Document name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={styles.docInput} autoFocus />
                        <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className={styles.docSelect}>
                            <option value="passport">🛂 Passport / ID</option>
                            <option value="flight">✈️ Flight</option>
                            <option value="hotel">🏨 Hotel</option>
                            <option value="insurance">🛡️ Insurance</option>
                            <option value="ticket">🎫 Ticket</option>
                            <option value="other">📎 Other</option>
                        </select>
                        <input placeholder="Note (optional)" value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} className={styles.docInput} />
                        <div className={styles.docFormActions}>
                            <button className={styles.docFormCancel} onClick={() => setShowForm(false)}>Cancel</button>
                            <button className={styles.docFormSave} onClick={addDoc}>Save</button>
                        </div>
                    </div>
                ) : (
                    <button className={styles.docAddBtn} onClick={() => setShowForm(true)}>
                        <span className="material-symbols-outlined">add</span> Add Document
                    </button>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   5. EMERGENCY INFO TOOL
   ============================================================ */
function EmergencyTool() {
    const numbers = [
        { label: 'Police', number: '911 / 112', icon: 'local_police', color: '#3b82f6' },
        { label: 'Ambulance', number: '911 / 112', icon: 'emergency', color: '#ef4444' },
        { label: 'Fire Department', number: '911 / 112', icon: 'local_fire_department', color: '#f97316' },
    ];

    const tips = [
        'Save your embassy\'s number before traveling',
        'Keep a photo of your passport on your phone',
        'Share your itinerary with family or friends',
        'Know the local equivalent of 911',
        'Register with your country\'s travel advisory program',
        'Keep emergency cash in a separate location',
    ];

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' }}>
                <span className={styles.toolHeaderEmoji}>🚨</span>
                <div>
                    <h2>Emergency Info</h2>
                    <p>Quick access to important contacts</p>
                </div>
            </div>
            <div className={styles.emergencyBody}>
                <div className={styles.emergencyNumbers}>
                    {numbers.map((n, i) => (
                        <a key={i} href={`tel:${n.number.split(' / ')[0]}`} className={styles.emergencyCard}>
                            <div className={styles.emergencyIconWrap} style={{ background: `${n.color}15`, color: n.color }}>
                                <span className="material-symbols-outlined">{n.icon}</span>
                            </div>
                            <div>
                                <span className={styles.emergencyLabel}>{n.label}</span>
                                <span className={styles.emergencyNumber}>{n.number}</span>
                            </div>
                            <span className="material-symbols-outlined" style={{ marginLeft: 'auto', color: '#d1d5db', fontSize: '1.25rem' }}>call</span>
                        </a>
                    ))}
                </div>
                <div className={styles.emergencyTips}>
                    <h3>Safety Tips</h3>
                    {tips.map((tip, i) => (
                        <div key={i} className={styles.emergencyTip}>
                            <span className="material-symbols-outlined" style={{ color: '#f59e0b', fontSize: '1.125rem' }}>lightbulb</span>
                            <span>{tip}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ============================================================
   6. CULTURE TIPS TOOL
   ============================================================ */
function CultureTool() {
    const tips = [
        { icon: '🤝', title: 'Greetings', desc: 'Research local greeting customs — some cultures prefer bows, others handshakes or cheek kisses.' },
        { icon: '👗', title: 'Dress Code', desc: 'Some religious or cultural sites require modest clothing. Pack a scarf or long pants just in case.' },
        { icon: '💰', title: 'Tipping', desc: 'Tipping customs vary widely. In some countries it\'s expected, in others it\'s considered rude.' },
        { icon: '📸', title: 'Photography', desc: 'Always ask before taking photos of people. Some areas and buildings prohibit photography.' },
        { icon: '🍽️', title: 'Dining Etiquette', desc: 'Table manners differ — learn whether using hands, chopsticks, or specific utensils is the norm.' },
        { icon: '⏰', title: 'Punctuality', desc: 'Attitudes toward time differ. Being "fashionably late" is okay in some cultures, rude in others.' },
        { icon: '🗣️', title: 'Communication', desc: 'Learn a few basic words in the local language. Even a simple "hello" or "thank you" means a lot.' },
        { icon: '🚫', title: 'Taboos', desc: 'Research local taboos — gestures, topics, or behaviors that might be offensive.' },
    ];

    return (
        <div className={styles.toolContent}>
            <div className={styles.toolHeader} style={{ background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)' }}>
                <span className={styles.toolHeaderEmoji}>🏛️</span>
                <div>
                    <h2>Culture Tips</h2>
                    <p>Respect local customs & traditions</p>
                </div>
            </div>
            <div className={styles.cultureBody}>
                {tips.map((tip, i) => (
                    <div key={i} className={styles.cultureCard}>
                        <span className={styles.cultureIcon}>{tip.icon}</span>
                        <div>
                            <h4>{tip.title}</h4>
                            <p>{tip.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ============================================================
   TOOL RENDERER
   ============================================================ */
function ToolRenderer({ toolId }) {
    switch (toolId) {
        case 'packing': return <PackingListTool />;
        case 'currency': return <CurrencyTool />;
        case 'phrases': return <PhrasesTool />;
        case 'documents': return <DocumentsTool />;
        case 'emergency': return <EmergencyTool />;
        case 'culture': return <CultureTool />;
        default: return null;
    }
}

/* ============================================================
   MAIN PAGE COMPONENT
   ============================================================ */
export function TripTools() {
    const [activeTool, setActiveTool] = useState('packing');
    const [mobileShowContent, setMobileShowContent] = useState(false);

    const handleToolClick = (toolId) => {
        setActiveTool(toolId);
        setMobileShowContent(true);
    };

    return (
        <div className={styles.root}>
            {/* Header */}
            <header className={styles.header}>
                <Link to="/add_expense" className={styles.backBtn}>
                    <span className="material-symbols-outlined">arrow_back</span>
                </Link>
                <div className={styles.headerText}>
                    <h1>Trip Toolkit</h1>
                    <p>Everything you need for your journey</p>
                </div>
            </header>

            {/* Main Layout */}
            <div className={styles.layout}>
                {/* Content Panel (left) */}
                <div className={`${styles.contentPanel} ${mobileShowContent ? styles.mobileShow : ''}`}>
                    <button className={styles.mobileBackToList} onClick={() => setMobileShowContent(false)}>
                        <span className="material-symbols-outlined">arrow_back</span>
                        <span>All Tools</span>
                    </button>
                    <ToolRenderer toolId={activeTool} />
                </div>

                {/* Sidebar (right) */}
                <nav className={`${styles.sidebar} ${mobileShowContent ? styles.mobileHide : ''}`}>
                    <div className={styles.sidebarTitle}>
                        <span className="material-symbols-outlined">apps</span>
                        <span>Tools</span>
                    </div>
                    <div className={styles.toolList}>
                        {TOOLS.map(tool => (
                            <button
                                key={tool.id}
                                className={`${styles.toolItem} ${activeTool === tool.id ? styles.toolItemActive : ''}`}
                                onClick={() => handleToolClick(tool.id)}
                            >
                                <div className={styles.toolItemIcon} style={activeTool === tool.id ? { background: tool.color, boxShadow: `0 4px 12px ${tool.color}40` } : undefined}>
                                    <span>{tool.icon}</span>
                                </div>
                                <span className={styles.toolItemLabel}>{tool.label}</span>
                                <span className={`material-symbols-outlined ${styles.toolItemArrow}`}>chevron_right</span>
                            </button>
                        ))}
                    </div>
                    <div className={styles.comingSoon}>
                        <span className={styles.comingSoonLabel}>Coming Soon</span>
                        <div className={styles.comingSoonList}>
                            <span className={styles.comingSoonItem}>🗺️ Offline Maps</span>
                            <span className={styles.comingSoonItem}>📸 Photo Journal</span>
                            <span className={styles.comingSoonItem}>👥 Group Split</span>
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    );
}
